// Deck, reel strips, stops, visible grid and peek rows (docs/04 §1).
// Two rules reshape what a reel shows: the Mirror Man (right half mirrors the left) and the
// Night Shift's sticky Grin column.
import { B } from '../content/balance';
import { activeRules } from './reckoning';
import type { Rng } from './rng';
import type { RunState, SizeId, SymbolInstance } from './types';

/** uid of the virtual Grin that fills the Night Shift's wild column. */
export const WILD_UID = -1;
const WILD_INST: SymbolInstance = { uid: WILD_UID, defId: 'grin', bonusValue: 0, age: 0, counters: {}, flags: 0 };

export function dims(size: SizeId): { reels: number; rows: number } {
  return B.SIZES[size];
}

export function mod(i: number, n: number): number {
  return ((i % n) + n) % n;
}

/** Shuffle the deck independently for every reel (rebuilt at each window start). */
export function buildStrips(run: RunState, rng: Rng): void {
  const { reels, rows } = dims(run.size);
  const uids = run.deck.map((d) => d.uid);
  run.strips = [];
  for (let r = 0; r < reels; r++) {
    let strip = rng.shuffle([...uids]);
    // Guard: tiny decks repeat so a column plus peek rows never shows the same slot twice.
    while (strip.length > 0 && strip.length < rows + 2) strip = strip.concat(rng.shuffle([...uids]));
    run.strips.push(strip);
  }
  run.stops = run.strips.map(() => 0);
}

/** Mid-window deck addition: insert at a random position per reel without disturbing the rest. */
export function insertIntoStrips(run: RunState, uid: number, rng: Rng): void {
  run.strips.forEach((strip, reel) => {
    const at = rng.int(strip.length + 1);
    strip.splice(at, 0, uid);
    if (at <= (run.stops[reel] ?? 0) && strip.length > 1) run.stops[reel] = (run.stops[reel] ?? 0) + 1;
  });
}

/** Mid-window removal: splice every occurrence out, keeping stops pointing at the same symbols. */
export function removeFromStrips(run: RunState, uid: number): void {
  run.strips.forEach((strip, reel) => {
    for (let i = strip.length - 1; i >= 0; i--) {
      if (strip[i] !== uid) continue;
      strip.splice(i, 1);
      const stop = run.stops[reel] ?? 0;
      if (i < stop) run.stops[reel] = stop - 1;
    }
    if (strip.length === 0) run.stops[reel] = 0;
    else run.stops[reel] = mod(run.stops[reel] ?? 0, strip.length);
  });
}

interface DeckCache {
  len: number;
  first: number;
  last: number;
  map: Map<number, SymbolInstance>;
}
// Every dry-run evaluation needs uid → instance; a command runs dozens of them on one deck.
// Cached per deck array (each command clones the deck, so the cache never outlives it) and
// re-validated against the deck's length and ends in case the array was spliced in place.
const deckCache = new WeakMap<SymbolInstance[], DeckCache>();

export function deckIndex(run: RunState): Map<number, SymbolInstance> {
  const deck = run.deck;
  const first = deck[0]?.uid ?? -2;
  const last = deck[deck.length - 1]?.uid ?? -2;
  let c = deckCache.get(deck);
  if (!c || c.len !== deck.length || c.first !== first || c.last !== last) {
    c = { len: deck.length, first, last, map: new Map(deck.map((d) => [d.uid, d])) };
    deckCache.set(deck, c);
  }
  if (wildColumn(run) === null) return c.map;
  const m = new Map(c.map);
  m.set(WILD_UID, { ...WILD_INST, counters: {} });
  return m;
}

/** The Night Shift column that is all Grins this spin, or null. */
export function wildColumn(run: RunState): number | null {
  const ns = run.nightShift;
  return ns && ns.left > 0 && run.flags?.nightSpin === run.spinIndex ? ns.wild : null;
}

/** The reel whose symbols reel `reel` shows (Mirror Man: right half mirrors the left). */
export function sourceReel(run: RunState, reel: number): number {
  const { reels } = dims(run.size);
  if (reel < Math.ceil(reels / 2) || !activeRules(run).some((r) => r.mirror)) return reel;
  return reels - 1 - reel;
}

/** Reels whose own stop doesn't matter this spin (mirrored or wild): Fate, holds and nudges skip them. */
export function passiveReel(run: RunState, reel: number): boolean {
  return sourceReel(run, reel) !== reel || wildColumn(run) === reel;
}

/** uid at strip position (wrapping). */
export function uidAt(run: RunState, reel: number, pos: number): number {
  const strip = run.strips[reel]!;
  return strip[mod(pos, strip.length)]!;
}

/** Visible uids as [reel][row] for the given stops. */
export function visibleUids(run: RunState, stops: number[]): number[][] {
  const { reels, rows } = dims(run.size);
  const wild = wildColumn(run);
  const out: number[][] = [];
  for (let r = 0; r < reels; r++) {
    const col: number[] = [];
    const src = sourceReel(run, r);
    for (let y = 0; y < rows; y++) col.push(r === wild ? WILD_UID : uidAt(run, src, (stops[src] ?? 0) + y));
    out.push(col);
  }
  return out;
}

/**
 * defIds of one column including `peek` rows above and below: length rows + 2·peek. `stops` gives
 * the other reels' stops so a mirrored reel can show its source.
 */
export function columnDefs(run: RunState, reel: number, stop: number, peek: number, stops?: number[]): string[] {
  const { rows } = dims(run.size);
  const idx = deckIndex(run);
  const src = sourceReel(run, reel);
  const at = src === reel ? stop : (stops?.[src] ?? run.stops[src] ?? 0);
  const wild = wildColumn(run) === reel;
  const out: string[] = [];
  for (let y = -peek; y < rows + peek; y++) out.push(wild && y >= 0 && y < rows ? 'grin' : (idx.get(uidAt(run, src, at + y))?.defId ?? 'cherry'));
  return out;
}

export function newInstance(run: RunState, defId: string): SymbolInstance {
  const inst: SymbolInstance = { uid: run.nextUid++, defId, bonusValue: 0, age: 0, counters: {}, flags: 0 };
  return inst;
}
